<script setup lang="ts">
import { computed } from 'vue';

// `item` is Kendo's item-render props object, forwarded whole: it carries id, index,
// dataItem, textField, focused, selected, itemClass and an onClick listener. Taking it
// as one prop keeps this component in step with ListItem.mjs without restating its shape.
const props = defineProps<{ item: any }>();

const name = computed(() => props.item?.dataItem?.[props.item?.textField ?? 'name'] ?? '');

// Every finder wants the same source footer, so the panel renders it rather than making
// eight callers pass an identical slot. The slot remains for anything that needs to differ.
const source = computed(() => props.item?.dataItem?.source?.name ?? '');
</script>

<template>
  <!-- position: unset reproduces Kendo's stock ListItem. The theme sets
       .k-list-item { position: relative } so .k-first::before can place a top border;
       stock lists neutralise it, and dropping this would surface a border they don't show. -->
  <li
      :id="item.id"
      role="option"
      :aria-selected="item.selected"
      :class="item.itemClass"
      class="finder-item"
      style="position: unset"
      @click="(event) => item.onClick?.(event)"
  >
    <!-- Deliberately NOT .k-list-item-text: that is Kendo's single-line text element
         (nowrap + ellipsis, plus a ::before zero-width space to establish a line box).
         In this flex column the pseudo-element becomes a flex item and eats a full line
         above the name. Nothing in Kendo queries the class, and the spans below already
         truncate per line, so it buys nothing here. -->
    <span class="finder-item-body">
      <span class="finder-item-head">
        <span class="finder-item-name">{{ name }}</span>
        <slot name="badge"></slot>
      </span>
      <span v-if="$slots.details" class="finder-item-details"><slot name="details"></slot></span>
      <span v-if="source || $slots.footer" class="finder-item-footer">
        <slot name="footer"><em>{{ source }}</em></slot>
      </span>
    </span>
  </li>
</template>
